/**
 * PTEL Visor - Map Attribution Notes
 * F8.25 — Panel colapsable de atribución de fuentes de datos
 *
 * Muestra licencias y procedencia de todos los datasets usados
 * en el visor cartográfico. Cumplimiento Plan Maestro B.4 + F10.03.
 *
 * Todos los datos son 100% públicos con licencias abiertas
 * (CC BY 4.0 / INSPIRE / ODbL).
 *
 * PTEL Design System v2: fondo #262624, texto #a8a8a0
 */

import React, { memo, useState } from 'react';

// =============================================================================
// TIPOS
// =============================================================================

export interface DataSource {
  /** Identificador único */
  id: string;
  /** Nombre del dataset */
  name: string;
  /** Entidad responsable */
  entity: string;
  /** Tipo de licencia */
  license: string;
  /** URL de referencia */
  url: string;
}

export interface MapAttributionNotesProps {
  /** Posición CSS del panel */
  position?: 'bottom-left' | 'bottom-right';
  /** Colapsado inicialmente (default: true) */
  defaultCollapsed?: boolean;
  /** Fuentes activas a mostrar (ids). Vacío o undefined = todas */
  activeSources?: string[];
}

// =============================================================================
// FUENTES DE DATOS
// =============================================================================

export const DATA_SOURCES: DataSource[] = [
  { id: 'cdau', name: 'CDAU', entity: 'Junta de Andalucía', license: 'CC BY 4.0', url: 'https://callejero.juntadeandalucia.es' },
  { id: 'nga', name: 'NGA', entity: 'IGN España', license: 'CC BY 4.0', url: 'https://centrodedescargas.cnig.es' },
  { id: 'dera', name: 'DERA', entity: 'IECA (Junta)', license: 'CC BY 4.0', url: 'https://www.juntadeandalucia.es/IECA' },
  { id: 'catastro', name: 'Catastro', entity: 'DG Catastro', license: 'INSPIRE', url: 'https://www.catastro.meh.es' },
  { id: 'agol', name: 'AGOL PTEL', entity: 'Junta de Andalucía', license: 'CC BY 4.0', url: 'https://www.arcgis.com' },
  { id: 'cartociudad', name: 'CartoCiudad', entity: 'IGN España', license: 'CC BY 4.0', url: 'https://www.cartociudad.es' },
  { id: 'tiles-ign', name: 'Tiles IGN', entity: 'IGN España', license: 'CC BY 4.0', url: 'https://www.ign.es' },
  { id: 'tiles-osm', name: 'Tiles OSM', entity: 'OpenStreetMap', license: 'ODbL', url: 'https://www.openstreetmap.org' },
  { id: 'osm-security', name: 'OSM Security', entity: 'OpenStreetMap', license: 'ODbL', url: 'https://www.openstreetmap.org' },
  { id: 'educacion', name: 'Centros educativos', entity: 'Min. Educación', license: 'CC BY 4.0', url: 'https://www.educacion.gob.es' },
  { id: 'gc-facilities', name: 'GC Facilities', entity: 'Guardia Civil', license: 'Público', url: 'https://www.guardiacivil.es' },
];

// =============================================================================
// ESTILOS (DS v2 tokens)
// =============================================================================

const COLORS = {
  bgPrimary: '#262624',
  fgPrimary: '#fafaf9',
  fgSecondary: '#a8a8a0',
  border: '#3f3f3c',
  primary: '#2dd4bf',
  muted: '#6b6b63',
};

const PANEL_STYLE: React.CSSProperties = {
  background: `rgba(38, 38, 36, 0.95)`,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 8,
  padding: 0,
  minWidth: 220,
  maxWidth: 320,
  color: COLORS.fgSecondary,
  fontSize: '0.75rem',
  fontFamily: "'Space Grotesk', sans-serif",
  zIndex: 1000,
  userSelect: 'none',
  backdropFilter: 'blur(4px)',
};

const HEADER_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  borderBottom: `1px solid rgba(255,255,255,0.05)`,
  cursor: 'pointer',
};

const BODY_STYLE: React.CSSProperties = {
  padding: '4px 12px 8px',
  maxHeight: 280,
  overflowY: 'auto',
};

const SOURCE_STYLE: React.CSSProperties = {
  padding: '5px 0',
  borderBottom: `1px solid rgba(255,255,255,0.03)`,
};

const LINK_STYLE: React.CSSProperties = {
  color: COLORS.primary,
  textDecoration: 'none',
  fontWeight: 500,
};

const FOOTER_STYLE: React.CSSProperties = {
  padding: '6px 12px',
  borderTop: `1px solid rgba(255,255,255,0.05)`,
  fontSize: '0.65rem',
  color: COLORS.muted,
  fontStyle: 'italic',
};

const POSITION_MAP: Record<string, React.CSSProperties> = {
  'bottom-left': { position: 'absolute', bottom: 8, left: 8 },
  'bottom-right': { position: 'absolute', bottom: 8, right: 8 },
};

// =============================================================================
// COMPONENTE
// =============================================================================

export const MapAttributionNotes = memo(function MapAttributionNotes({
  position = 'bottom-left',
  defaultCollapsed = true,
  activeSources,
}: MapAttributionNotesProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const sources = activeSources && activeSources.length > 0
    ? DATA_SOURCES.filter((s) => activeSources.includes(s.id))
    : DATA_SOURCES;

  return (
    <div
      style={{ ...PANEL_STYLE, ...POSITION_MAP[position] }}
      role="region"
      aria-label="Atribución de fuentes de datos"
    >
      {/* Header */}
      <div
        style={HEADER_STYLE}
        onClick={() => setCollapsed(!collapsed)}
        role="button"
        aria-expanded={!collapsed}
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setCollapsed(!collapsed)}
      >
        <span style={{ fontWeight: 600, color: COLORS.fgPrimary }}>
          ℹ️ Fuentes de datos
        </span>
        <span style={{ fontSize: '0.7rem', color: COLORS.muted }}>
          {collapsed ? '▸' : '▾'}
        </span>
      </div>

      {/* Body */}
      {!collapsed && (
        <>
          <div style={BODY_STYLE}>
            {sources.map((src) => (
              <div key={src.id} style={SOURCE_STYLE}>
                <div>
                  <a
                    href={src.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={LINK_STYLE}
                  >
                    📍 {src.name}
                  </a>
                  <span style={{ color: COLORS.fgSecondary }}> · {src.entity}</span>
                </div>
                <div style={{ fontSize: '0.65rem', color: COLORS.muted, paddingTop: 1 }}>
                  {src.license}
                </div>
              </div>
            ))}
          </div>

          <div style={FOOTER_STYLE}>
            Todos los datos son públicos con licencias abiertas.
          </div>
        </>
      )}
    </div>
  );
});

export default MapAttributionNotes;
